import React from "react";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "ary_cookie_consent_v1";

type ConsentMode = "necessary" | "all";

function readConsent(): ConsentMode | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { mode?: ConsentMode };
    if (parsed?.mode === "necessary" || parsed?.mode === "all") return parsed.mode;
  } catch {
    return null;
  }
  return null;
}

function persistConsent(mode: ConsentMode) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    COOKIE_CONSENT_KEY,
    JSON.stringify({
      mode,
      updatedAt: new Date().toISOString(),
    })
  );
}

export default function CookieBanner() {
  const [visible, setVisible] = React.useState(false);
  const [preferencesOpen, setPreferencesOpen] = React.useState(false);

  React.useEffect(() => {
    const consent = readConsent();
    setVisible(consent === null);
  }, []);

  React.useEffect(() => {
    const openPreferences = () => {
      setPreferencesOpen(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-preferences", openPreferences as EventListener);
    return () => window.removeEventListener("open-cookie-preferences", openPreferences as EventListener);
  }, []);

  if (!visible) return null;

  const handleNecessary = () => {
    persistConsent("necessary");
    setPreferencesOpen(false);
    setVisible(false);
  };

  const handleAcceptAll = () => {
    persistConsent("all");
    setPreferencesOpen(false);
    setVisible(false);
  };

  return (
    <>
      <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Aviso de cookies">
        <p className="cookie-banner-text">
          Usamos cookies técnicas necesarias para el funcionamiento de la app. No usamos cookies de analítica o marketing por ahora.
        </p>
        <div className="cookie-banner-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setPreferencesOpen(true)}>
            Configurar
          </button>
          <button type="button" className="btn btn-light" onClick={handleNecessary}>
            Solo necesarias
          </button>
          <button type="button" className="btn btn-primary" onClick={handleAcceptAll}>
            Aceptar
          </button>
        </div>
        <p className="cookie-banner-link">
          <Link to="/legal/privacidad-cookies" target="_blank" rel="noopener noreferrer">
            Ver política de privacidad y cookies
          </Link>
        </p>
      </div>

      {preferencesOpen && (
        <div className="cookie-modal-wrap" role="dialog" aria-modal="true" aria-label="Preferencias de cookies">
          <button type="button" className="cookie-modal-backdrop" onClick={() => setPreferencesOpen(false)} aria-label="Cerrar" />
          <div className="cookie-modal">
            <h3>Preferencias de cookies</h3>
            <p>
              Puedes decidir qué categorías activar. Actualmente esta web solo utiliza cookies técnicas necesarias.
            </p>
            <div className="cookie-pref-list">
              <label className="cookie-pref-item">
                <span>
                  <strong>Técnicas (necesarias)</strong>
                  <small>Permiten login, seguridad y funcionamiento básico.</small>
                </span>
                <input type="checkbox" checked disabled />
              </label>
              <label className="cookie-pref-item cookie-pref-item--disabled">
                <span>
                  <strong>Analíticas</strong>
                  <small>No activas en este momento.</small>
                </span>
                <input type="checkbox" disabled />
              </label>
              <label className="cookie-pref-item cookie-pref-item--disabled">
                <span>
                  <strong>Marketing</strong>
                  <small>No activas en este momento.</small>
                </span>
                <input type="checkbox" disabled />
              </label>
            </div>
            <div className="cookie-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setPreferencesOpen(false)}>
                Cerrar
              </button>
              <button type="button" className="btn btn-light" onClick={handleNecessary}>
                Guardar solo necesarias
              </button>
              <button type="button" className="btn btn-primary" onClick={handleAcceptAll}>
                Aceptar y guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
